import React, { FC, memo, ReactNode, useMemo } from 'react';
import { SC } from '../../styles/theme';
import { useTranslations } from '@hooks/useTranslations';
import Link from '@components/Link';

type IdMyAccount = 'my-account';
type IdMySaves = 'my-saves';
type IdMyOrders = 'my-orders';
type IdLogout = 'logout';

interface ItemIds {
    id: IdMyAccount | IdMySaves | IdMyOrders | IdLogout;
    dataTestId?: string;
}
interface ItemInteractive {
    label: string;
    isActive?: boolean;
    total?: number;
}

interface ItemWrapper extends ItemIds {
    type: 'wrapper';
    children: ReactNode;
}
interface ItemLink extends ItemIds, ItemInteractive {
    type: 'link';
    href: string;
}
interface ItemClick extends ItemIds, ItemInteractive {
    type: 'click';
    onClick(): void;
}

export type MenuItem = ItemLink | ItemClick | ItemWrapper;

export const MenuItemTemplate: FC<MenuItem & SC> = memo(({ className, children, ...item }) => {
    const { t } = useTranslations();
    const ItemLink = useMemo(() => {
        if (item.type === 'wrapper') return null;

        const TotalNumber = item.total === undefined ? null : ` (${item.total})`;

        if (item.total === 0) {
            return (
                <Link
                    href="#null"
                    label={`${t(item.label)} ${TotalNumber || ''} `}
                    primary
                    withArrowIcon={!!item.total}
                    marginTop={10}
                />
            );
        }

        if (item.type === 'click') {
            return (
                <Link
                    onClick={item.onClick}
                    href="#null"
                    label={`${t(item.label)} ${TotalNumber || ''} `}
                    primary
                    withArrowIcon={!!item.total}
                    marginTop={10}
                />
            );
        }

        return (
            <Link
                href={item.href}
                label={`${t(item.label)} ${TotalNumber || ''} `}
                primary
                withArrowIcon={!!item.total}
                marginTop={10}
            />
        );
    }, [t, item]);

    return (
        <li
            className={`${className}${item.type !== 'wrapper' && item.isActive ? ' active' : ''}`}
            data-testId={item.dataTestId}
        >
            {children || ItemLink}
        </li>
    );
});
MenuItemTemplate.displayName = 'MenuItemTemplate';
